'use client'
import { SearchType, searchTypes } from '@/app/(site)/search/types'
import { getChannelGifsUrl, getChannelUrl } from '@/app/components/collections/urls'
import { AdEndpointViews, getAdDecisionByType, getClickableAd, hasAd } from '@/app/util/ads-endpoint'
import { ChannelGifsOptions } from '@/app/util/channel-api'
import { publicRuntimeConfig } from '@/app/util/env'
import createPlaceholderGif from '@/app/util/gif-skeleton'
import { processSearchTerm } from '@/app/util/search-term'
import { useGiphyFetch } from '@/app/util/use-gf'
import AdsContext from '@/context/ads'
import { useClientSideAPIKey } from '@/util/url'
import { Result, SearchOptions } from '@giphy/js-fetch-api'
import { IChannel, IGif } from '@giphy/js-types'
import { useParams, useSearchParams } from 'next/navigation'
import { useContext, useMemo } from 'react'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'
import { GenericResult } from 'ui/src/hooks/use-fetch-data'
import { insertItem, replaceItem } from 'utils'

const apiUrlV2 = publicRuntimeConfig.apiUrl.replace('v1', 'v2')

type TrendingSearch = { name: string; analytics_response_payload: string }

const getParams = (obj: any) => new URLSearchParams(obj).toString()

export function useGif(id?: string) {
    const gf = useGiphyFetch()
    return useSWR<IGif>(id ? id : null, (id: string) => gf.gif(id).then((res) => res.data))
}

/**
 * If there is a ad, insert a placeholder into a gifs array
 * @param gifs the gifs array to insert into
 * @param placeholderId the id of the placeholder, to be used later to remove it
 * @param targetIndex the location of the placeholder in the gifs array, default 0
 * @returns
 */
export function useGifsWithPlaceholder(
    gifs: IGif[],
    placeholderId: string,
    view?: AdEndpointViews,
    blockPlaceholder: boolean = false,
    targetIndex: number = 0
) {
    const { adServerData, adClientData } = useContext(AdsContext)
    const searchType = useSearchType()

    const hasClickableAd = view && (getClickableAd(adServerData, view) || getClickableAd(adClientData, view))
    const shouldInsertPlaceholder =
        !blockPlaceholder && view && !hasClickableAd
            ? // we have ad data for this view in the server request
              (hasAd(view, adServerData) &&
                  searchType === 'gifs' &&
                  // we don't already have it on the client
                  getAdDecisionByType(adClientData, view)?.gif === undefined &&
                  // the placeholder doesn't exist already
                  !gifs.some((g) => g.id === placeholderId) &&
                  // we are showing this index
                  targetIndex < gifs.length) ||
              (getAdDecisionByType(adClientData, view) && searchType === 'gifs')
            : false
    return useMemo(() => {
        const placeHolder = createPlaceholderGif({ id: placeholderId }, { width: 250, height: 250 })
        return shouldInsertPlaceholder ? insertItem(gifs, targetIndex, placeHolder) : gifs
    }, [gifs, shouldInsertPlaceholder, targetIndex, placeholderId])
}

/**
 *
 * @param setGifs
 * @param placeholderId
 * @returns
 */
export function useGifUpdater(gifs: IGif[], placeholderId: string, view: AdEndpointViews) {
    const { adClientData } = useContext(AdsContext)

    // AdsContext will fetch a gif for the banner and grid
    return useMemo(() => {
        const adGif = getAdDecisionByType(adClientData, view)?.gif
        if (adGif) {
            const placeholderIndex = gifs.findIndex((g) => g.id === placeholderId)
            if (placeholderIndex !== -1) {
                return replaceItem(gifs, placeholderIndex, adGif)
            }
        }
        return gifs
    }, [gifs, placeholderId, view, adClientData])
}

export function useSearchType(): SearchType {
    const params = useParams()

    // search params are on the homepage: ?type=stickers
    const searchParams = useSearchParams()
    const searchParamType = searchParams.get('type')?.toLowerCase() as any
    if (searchTypes.includes(searchParamType)) {
        return searchParamType
    }
    // params are on search pages: pizza-stickers
    const [, type] = params.term ? processSearchTerm(params.term as string) : []
    if (searchTypes.includes(type as any)) {
        return type as SearchType
    }
    return 'gifs'
}

export function useImmutableChannel(channelId: number | string | null = null) {
    // we will load an immutable channel and apply mutations through the SWR mutate api
    return useSWRImmutable<IChannel>(channelId ? getChannelUrl(channelId) : null)
}

export function useChannelWithFallback(channel: IChannel) {
    return useSWRImmutable<IChannel>(getChannelUrl(channel.id), null, {
        fallback: { [getChannelUrl(channel.id)]: channel },
    })
}

export function useChannelGifs(channelId: number | string | null = null, options: ChannelGifsOptions) {
    return useSWRImmutable<IChannel>(channelId ? getChannelGifsUrl(channelId, options) : null)
}

export const useTrendingSearches = (term: string = '', options: SearchOptions = {}) =>
    useSWR<GenericResult<TrendingSearch>>(
        `${apiUrlV2}trending/searches?${getParams({
            ...options,
            q: term,
            api_key: useClientSideAPIKey(),
        })}`
    )

export const useSearchChannel = (channelName: string, options: SearchOptions = {}) => {
    const api_key = useClientSideAPIKey()
    return useSWR<GenericResult<IChannel>>(
        channelName
            ? `${publicRuntimeConfig.apiUrl}channels/search?${getParams({
                  ...options,
                  q: channelName.replace('@', ''),
                  api_key,
              })}`
            : null
    )
}

export const useSearchChannelByTerm = (term: string, options: SearchOptions = {}) =>
    useSWR<GenericResult<IChannel>>(
        `${publicRuntimeConfig.apiUrl}channels/search?${getParams({
            ...options,
            q: term,
            api_key: useClientSideAPIKey(),
        })}`
    )

type SearchName = { analytics_response_payload: string; name: string; response_id: string }
type SearchTagsResult = { data: SearchName[] } & Result
export const useSearchTags = (term: string, options: SearchOptions = {}) =>
    useSWR<SearchTagsResult>(
        `${publicRuntimeConfig.apiUrl}gifs/search/tags?${getParams({
            ...options,
            q: term,
            api_key: useClientSideAPIKey(),
        })}`
    )
